import React, { useEffect, useState } from 'react';
import './LoanOffersPage.css';
import { fetchDashboardData } from '../../services/api'; 
import { useNavigate } from 'react-router-dom';
import { Header, SideMenu, TopMenu } from 'maxcu-shared-lib';
import menuItems from '../SideMenu/menuItems';



const LoanOffersPage = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creditTier, setCreditTier] = useState('B+'); // Default to A+ tier
  const [isCollapsed, setIsCollapsed] = useState(false);
  useEffect(() => {
    // Set the browser title
    document.title = 'My Offers | MAX Credit Union';
  }, []);

  const handleResize = () => {
    if (window.innerWidth < 600) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  };

  useEffect(() => {
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [offers, setOffers] = useState({
    new_vehicle_offer: false,
    used_vehicle_offer: false,
    max_signature_offer: false,
    platinum_visa_offer: false,
    smart_rewards_offer: false,
    unsecured_loan_offer: false,
    checking_offer: false,
    wealth_management_offer: false,
    insurance_offer: false,
    HELOC_offer: false,
  });
  const [offersData, setOffersData] = useState({});
  
  // State for selected terms and rates
  const [selectedTerms, setSelectedTerms] = useState({
    new_vehicle: '',
    used_vehicle: '',
    unsecured_loan: '',
    HELOC: ''
  });

  // Helper function to get credit grade based on credit score
  const getCreditGrade = (creditScore) => {
    if (creditScore >= 735) return "A+";
    if (creditScore >= 690) return "A";
    if (creditScore >= 670) return "B+";
    if (creditScore >= 647) return "B";
    return null;
  };

  // Helper function to get loan data for a specific loan type and credit grade
  const getLoanData = (loanType, creditGrade) => {
    if (!dashboardData?.loanTypes?.[loanType]?.[creditGrade]) {
      
      return { rates: [], terms: [] };
    }
    
    const { terms, rates } = dashboardData.loanTypes[loanType][creditGrade];
    
    return { terms, rates };
  };

  // Process the dashboard data to extract offers and offers data
  const processDashboardData = (data) => {
    if (!data) return;

    // Ensure loanTypes data is available before processing
    if (!data.loanTypes) {
      return;
    }
    
    // Extract offer flags from MyOffer array
    const myOffer = data.MyOffer?.[0] || {};
    
    
    // Update offers based on flags
    const updatedOffers = {
      new_vehicle_offer: myOffer.AUTOFLAG?.trim() === "1",
      used_vehicle_offer: myOffer.AUTOFLAG?.trim() === "1",
      platinum_visa_offer: myOffer.CCFLAG?.trim() === "1",
      max_signature_offer: myOffer.PRODUCTFLAG2?.trim() === "1",
      HELOC_offer: myOffer.HELOC?.trim() === "1",
      checking_offer: myOffer.CheckingFLAG?.trim() === "1",
      wealth_management_offer: myOffer.WMFLAG?.trim() === "1",
      insurance_offer: myOffer.INSURANCEFLAP?.trim() === "1",
      smart_rewards_offer: myOffer.PRODUCTFLAG4?.trim() === "1",
      unsecured_loan_offer: myOffer.PLFLAG?.trim() === "1",
    };
    
    
    setOffers(updatedOffers);

    // Get credit score and determine credit grade
    const creditScore = parseInt(myOffer.CREDIT?.trim() || "0");
    const creditGrade = getCreditGrade(creditScore);
    
    
    
    if (!creditGrade) {
      
      return;
    }

    // Set the credit tier based on the credit grade
    setCreditTier(creditGrade);

    // Process offers data
    const processedOffersData = {};

    // Process Auto Loan Offers
    if (myOffer.AUTOFLAG?.trim() === "1") {
      const { terms, rates } = getLoanData("new_vehicle", creditGrade);
      
      // Create default terms and rates if none are available
      const defaultTerms = terms.length > 0 ? terms : [42, 66, 78, 84];
      const defaultRates = rates.length > 0 ? rates : [7.5, 7.99, 9.5, 9.99];
      
      processedOffersData.new_vehicle = {
        apr: defaultRates[0] ? `${defaultRates[0]}%` : "",
        term_rate: defaultTerms[0] ? `${defaultTerms[0]} months` : "",
        amount: "$0",
        payment: "$0",
        terms_with_rates: defaultTerms.map((term, index) => [`${term} months`, `${defaultRates[index]}%`]),
      };

      const usedTerms = getLoanData("used_vehicle", creditGrade);
      
      // Create default terms and rates if none are available
      const defaultUsedTerms = usedTerms.terms.length > 0 ? usedTerms.terms : [42, 66, 72];
      const defaultUsedRates = usedTerms.rates.length > 0 ? usedTerms.rates : [9.5, 9.99, 11.5];
      
      processedOffersData.used_vehicle = {
        apr: defaultUsedRates[0] ? `${defaultUsedRates[0]}%` : "",
        term_rate: defaultUsedTerms[0] ? `${defaultUsedTerms[0]} months` : "",
        amount: "$0",
        payment: "$0",
        terms_with_rates: defaultUsedTerms.map((term, index) => [`${term} months`, `${defaultUsedRates[index]}%`]),
      };
    }

    // Process Credit Card Offers
    if (myOffer.CCFLAG?.trim() === "1") {
      const visaRates = getLoanData("Visa", creditGrade).rates;
      processedOffersData.platinum_visa = {
        apr: visaRates[0] ? `${visaRates[0]}%` : "16.75%",
        credit_limit: "$9,999"
      };

      const mastercardRates = getLoanData("Mastercard", creditGrade).rates;
      processedOffersData.max_signature = {
        apr: mastercardRates[0] ? `${mastercardRates[0]}%` : "18.0%",
        credit_limit: "$99,999"
      };

      const smartRewardsRates = getLoanData("Smart Rewards", creditGrade).rates;
      processedOffersData.smart_rewards = {
        apr: smartRewardsRates[0] ? `${smartRewardsRates[0]}%` : "18.0%",
        credit_limit: "$9,999"
      };
    }

    // Process Personal Loan
    if (myOffer.PLFLAG?.trim() === "1") {
      const { terms, rates } = getLoanData("Personal Loans", creditGrade);
      
      // Create default terms and rates if none are available
      const defaultPLTerms = terms.length > 0 ? terms : [12, 24, 36, 42];
      const defaultPLRates = rates.length > 0 ? rates : [15.0, 15.0, 15.0, 15.0];
      
      processedOffersData.unsecured_loan = {
        apr: defaultPLRates[0] ? `${defaultPLRates[0]}%` : "",
        term_rate: defaultPLTerms[0] ? `${defaultPLTerms[0]} months` : "",
        amount: "$10,000",
        payment: "$299.99",
        terms_with_rates: defaultPLTerms.map((term, index) => [`${term} months`, `${defaultPLRates[index]}%`]),
      };
    }

    // Process HELOC
    if (myOffer.HELOC?.trim() === "1") {
      const { terms, rates } = getLoanData("HELOC", creditGrade);
      
      // Create default terms and rates if none are available
      const defaultHELOCTerms = terms.length > 0 ? terms : [12, 24, 36, 66];
      const defaultHELOCRates = rates.length > 0 ? rates : [6.0, 6.5, 7.25, 7.75];
      
      processedOffersData.HELOC = {
        apr: defaultHELOCRates[0] ? `${defaultHELOCRates[0]}%` : "",
        term_rate: defaultHELOCTerms[0] ? `${defaultHELOCTerms[0]} months` : "",
        amount: "$50,000",
        payment: "$249.99",
        terms_with_rates: defaultHELOCTerms.map((term, index) => [`${term} months`, `${defaultHELOCRates[index]}%`]),
      };
    }

    
    setOffersData(processedOffersData);
    
    // Set default selected terms
    const defaultSelectedTerms = {};
    if (processedOffersData.new_vehicle?.terms_with_rates?.length > 0) {
      defaultSelectedTerms.new_vehicle = processedOffersData.new_vehicle.terms_with_rates[0].join(',');
    }
    if (processedOffersData.used_vehicle?.terms_with_rates?.length > 0) {
      defaultSelectedTerms.used_vehicle = processedOffersData.used_vehicle.terms_with_rates[0].join(',');
    }
    if (processedOffersData.unsecured_loan?.terms_with_rates?.length > 0) {
      defaultSelectedTerms.unsecured_loan = processedOffersData.unsecured_loan.terms_with_rates[0].join(',');
    }
    if (processedOffersData.HELOC?.terms_with_rates?.length > 0) {
      defaultSelectedTerms.HELOC = processedOffersData.HELOC.terms_with_rates[0].join(',');
    }
    
    
    setSelectedTerms(defaultSelectedTerms);
  };

  // Handle term selection change
  const handleTermChange = (loanType, value) => {
    
    setSelectedTerms(prev => ({
      ...prev,
      [loanType]: value
    }));
  };

  // Calculate payment based on selected term and amount
  const calculatePayment = (loanType, amount) => {
    if (!amount || !selectedTerms[loanType]) return 0;
    
    const [term, rate] = selectedTerms[loanType].split(',');
    const termMonths = parseInt(term.split(' ')[0]);
    const annualRate = parseFloat(rate) / 100;
    const monthlyRate = annualRate / 12;
    
    // Simple loan payment calculation
    const payment = amount * monthlyRate * Math.pow(1 + monthlyRate, termMonths) / (Math.pow(1 + monthlyRate, termMonths) - 1);
    
    return isNaN(payment) ? 0 : payment.toFixed(2);
  };

  // Navigate to loan application with selected loan type and data
  const navigateToLoanApplication = (loanType, selectedAmount = null, selectedTerm = null, selectedRate = null) => {
    
    
    // Get the selected term and rate if not provided
    if (!selectedTerm || !selectedRate) {
      
      const [term, rate] = selectedTerms[loanType]?.split(',') || ['', ''];
      selectedTerm = term;
      selectedRate = rate;
      
    }

    // Map loan types to application types
    const loanTypeMap = {
      'unsecured_loan': 'Unsecured Loan Application',
      'max_signature': 'MAX Signature Application',
      'platinum_visa': 'Platinum Visa Application',
      'smart_rewards': 'Smart Rewards Application',
      'new_vehicle': 'New Vehicle Application',
      'used_vehicle': 'Used Vehicle Application'
    };

    const applicationType = loanTypeMap[loanType] || loanType;
    
    
    
    
    navigate('/loan-application', {
      state: {
        loanType: applicationType,
        selectedAmount: selectedAmount,
        selectedTerm: selectedTerm,
        selectedRate: selectedRate
      }
    });
  };

  // Add this function near your other handlers
  const handleCreditCardSubmit = (buttonId) => {
    // Map button IDs to application types
    const applicationTypeMap = {
      'applyMaxSignature': 'MAX Signature Application',
      'applyPlatinumVisa': 'Platinum Visa Application',
      'SmartRewards': 'Smart Rewards Application'
    };

    const applicationType = applicationTypeMap[buttonId];
    
    // Navigate to loan application with credit card specific data
    navigate('/loan-application', {
      state: {
        loanType: applicationType,
        selectedAmount: '0', // Default credit limit
        selectedTerm: 'N/A',    // Credit cards don't have terms
        selectedRate: offersData[buttonId]?.apr || '16.99%' // Use rate from offersData or default
      }
    });
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const data = await fetchDashboardData();
        
        setDashboardData(data);
        setError(null);
      } catch (err) {
        
        setError('Failed to load dashboard data. Please try again later.');
       
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Separate useEffect to process data after dashboardData is set
  useEffect(() => {
    if (dashboardData) {
      processDashboardData(dashboardData);
    }
  }, [dashboardData]);

  if (loading) {
    return <div className="loading">Loading offers...</div>;
  }

  if (error) {
    return (
      <div className="loan-offers-page">
        <SideMenu menuItems={menuItems} />
        {isCollapsed && <TopMenu />}
        <div className="main-content">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    
    return (
        <>
            <SideMenu menuItems={menuItems} />
            {isCollapsed && <TopMenu />}
            <div className="no-data" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: '100vh',
                width: '100%',
                fontSize: '1.2rem',
                color: '#666'
            }}>
                No offers available at this time.
            </div>
        </>
    );
  }

  return (
    <>
      <Header name="My Offers" />
      <div className="loan-offers-page">
        <SideMenu menuItems={menuItems} />
        {isCollapsed && <TopMenu />}
        <div className="main-content">
          <div className="centered-intro">
            <h4>You are invited to Apply!</h4>
            <p className="intro-text">We've selected these exclusive offers just for you with these estimated rates. Actual Annual Percentage (APR) rates may vary and will be based on your creditworthiness.</p>
          </div>
          <div className="offers">
            {offers.new_vehicle_offer && (
              <div className="offer">
                <h2>New Vehicle Loan</h2>
                <img src="/Car.png" alt="Car" />
                <p className="amount-label"><span>Select Estimated Term and Rate:</span></p>
                <select 
                  id="termSelectNewVehicle" 
                  name="termSelectNewVehicle"
                  value={selectedTerms.new_vehicle}
                  onChange={(e) => handleTermChange('new_vehicle', e.target.value)}
                  className="term-select"
                >
                  {offersData.new_vehicle?.terms_with_rates?.map((item, index) => {
                    const [term, rate] = item;
                    return (
                      <option key={index} value={`${term},${rate}`}>
                        {term} - {rate}
                      </option>
                    );
                  })}
                </select>
                <p>*Actual Annual Percentage Rate (APR) based on creditworthiness.</p>
                <p><span className="bold-text">Requested amount:</span></p>
                <div className="input-container">
                  <span className="currency-icon">$</span>
                  <input 
                    type="number" 
                    id="amountToLoanNewVehicle" 
                    name="amountToLoanNewVehicle"
                    step="0.01" 
                    placeholder="Enter Amount" 
                    required
                    className="amount-input"
                  />
                  <button 
                    id="calculateNewVehicle" 
                    className="calculate-btn"
                    onClick={() => {
                      const amount = document.getElementById('amountToLoanNewVehicle').value;
                      
                      
                      const payment = calculatePayment('new_vehicle', amount);
                      
                      
                      document.getElementById('new_vehicle_payment').textContent = `$${payment}`;
                      
                      // No need to enable any button since we're using the Debug button directly
                      
                    }}
                  >
                    Calculate
                  </button>
                </div>
                <p>Est. Monthly Payment: <br /> <span id="new_vehicle_payment">$0</span></p>
                {/* Debug button for New Vehicle - now styled as the Apply button */}
                <button 
                  id="debugNewVehicle" 
                  className="apply-btn" 
                  onClick={() => {
                    
                    const amount = document.getElementById('amountToLoanNewVehicle').value || '0';
                    const [term, rate] = selectedTerms.new_vehicle?.split(',') || ['36 months', '4.99%'];
                    
                    // Update navigation to auto-application
                    navigate('/auto-application', {
                      state: {
                        loanType: 'New Vehicle Application',
                        selectedAmount: amount,
                        selectedTerm: term,
                        selectedRate: rate
                      }
                    });
                  }}
                >
                  Apply
                </button>
              </div>
            )}

            {offers.used_vehicle_offer && (
              <div className="offer">
                <h2>Used Vehicle Loan</h2>
                <img src="/Car.png" alt="Car" />
                <p className="amount-label"><span>Select Estimated Term and Rate:</span></p>
                <select 
                  id="termSelectUsedVehicle" 
                  name="termSelectUsedVehicle"
                  value={selectedTerms.used_vehicle}
                  onChange={(e) => handleTermChange('used_vehicle', e.target.value)}
                  className="term-select"
                >
                  {offersData.used_vehicle?.terms_with_rates?.map((item, index) => {
                    const [term, rate] = item;
                    return (
                      <option key={index} value={`${term},${rate}`}>
                        {term} - {rate}
                      </option>
                    );
                  })}
                </select>
                <p>*Actual Annual Percentage Rate (APR) based on creditworthiness.</p>
                <p><span className="bold-text">Requested amount:</span></p>
                <div className="input-container">
                  <span className="currency-icon">$</span>
                  <input 
                    type="number" 
                    id="amountToLoanUsedVehicle" 
                    name="amountToLoanUsedVehicle"
                    step="0.01" 
                    placeholder="Enter Amount" 
                    required
                    className="amount-input"
                  />
                  <button 
                    id="calculateUsedVehicle" 
                    className="calculate-btn"
                    onClick={() => {
                      const amount = document.getElementById('amountToLoanUsedVehicle').value;
                      
                      
                      const payment = calculatePayment('used_vehicle', amount);
                      
                      
                      document.getElementById('used_vehicle_payment').textContent = `$${payment}`;
                      
                      // No need to enable any button since we're using the Debug button directly
                      
                    }}
                  >
                    Calculate
                  </button>
                </div>
                <p>Est. Monthly Payment: <br /> <span id="used_vehicle_payment">$0</span></p>
                {/* Debug button for Used Vehicle - now styled as the Apply button */}
                <button 
                  id="debugUsedVehicle" 
                  className="apply-btn" 
                  onClick={() => {
                    
                    const amount = document.getElementById('amountToLoanUsedVehicle').value || '';
                    const [term, rate] = selectedTerms.used_vehicle?.split(',') || ['36 months', '9.99%'];
                    
                    // Update navigation to auto-application
                    navigate('/auto-application', {
                      state: {
                        loanType: 'Used Vehicle Application',
                        selectedAmount: amount,
                        selectedTerm: term,
                        selectedRate: rate
                      }
                    });
                  }}
                >
                  Apply
                </button>
              </div>
            )}

            {offers.unsecured_loan_offer && (
              <div className="offer">
                <h2>Unsecured Loan</h2>
                
                <img src="/PL.png" alt="PL" />
                <p className="amount-label"><span>Select Estimated Term and Rate:</span></p>
                <select 
                  id="termSelectUnsecuredLoan" 
                  name="termSelectUnsecuredLoan"
                  value={selectedTerms.unsecured_loan}
                  onChange={(e) => handleTermChange('unsecured_loan', e.target.value)}
                  className="term-select"
                >
                  {offersData.unsecured_loan?.terms_with_rates?.map((item, index) => {
                    const [term, rate] = item;
                    return (
                      <option key={index} value={`${term},${rate}`}>
                        {term} - {rate} APR
                      </option>
                    );
                  })}
                </select>
                <p>*Actual Annual Percentage Rate (APR) based on creditworthiness.</p>
                
                <p><span className="bold-text">Requested amount:</span></p>
                <div className="input-container">
                  <span className="currency-icon">$</span>
                  <input 
                    type="number" 
                    id="amountToLoanUnsecuredLoan" 
                    name="amountToLoanUnsecuredLoan"
                    placeholder="Enter Amount" 
                    required
                    className="amount-input"
                  />
                  <button 
                    id="calculateUnsecuredLoan" 
                    className="calculate-btn"
                    onClick={() => {
                      const amount = document.getElementById('amountToLoanUnsecuredLoan').value;
                      
                      
                      const payment = calculatePayment('unsecured_loan', amount);
                      
                      
                      document.getElementById('unsecured_loan_payment').textContent = `$${payment}`;
                      
                      // No need to enable any button since we're using the Debug button directly
                      
                    }}
                  >
                    Calculate
                  </button>
                </div>
                <p>Est. Monthly Payment: <br /> <span id="unsecured_loan_payment">$0</span></p>
                <button 
                  id="debugUnsecuredLoan" 
                  className="apply-btn" 
                  onClick={() => {
                    
                    const amount = document.getElementById('amountToLoanUnsecuredLoan').value || '0';
                    const [term, rate] = selectedTerms.unsecured_loan?.split(',') || ['36 months', '7.99%'];
                    
                    navigate('/loan-application', {
                      state: {
                        loanType: 'Unsecured Loan Application',
                        selectedAmount: amount,
                        selectedTerm: term,
                        selectedRate: rate
                      }
                    });
                  }}
                >
                  Apply
                </button>
              </div>
            )}

            {offers.max_signature_offer && (
              <div className="offer">
                <h2>MAX Signature Credit Card</h2>
                <img src="/MASTER.png" alt="MASTER" />
                
                <p>Cash in on more.</p>
                <p>Put money back in your pocket with cash back on everyday purchases with a VISA Signature
                  Card. Our quick and easy application process takes just a few minutes to complete.</p>
                <p><strong>Good For:</strong> 
                  1.5% cash back on all purchases, 
                  Higher credit limit, 
                  Everyday purchases
                </p>
                <a href="https://www.mymax.com/assets/files/sCGyk5zF" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                <button 
                  id="applyMaxSignature" 
                  className="apply-btn"
                  onClick={() => handleCreditCardSubmit('applyMaxSignature')}
                >
                  Apply
                </button>
              </div>
            )}

            {offers.platinum_visa_offer && (
              <div className="offer">
                <h2>Platinum Visa Credit Card</h2>
                <img src="/VISA.png" alt="VISA" />
                <p>Simple and Secure</p>
                <p>Enjoy no annual fee, competitive rates, and the convenience and security you expect from
                  Visa. </p>
                
                <p><strong>Good For:</strong> Debt consolidation, Everyday purchases</p>
                <a href="https://www.mymax.com/assets/files/sCGyk5zF" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                <button 
                  id="applyPlatinumVisa" 
                  className="apply-btn"
                  onClick={() => handleCreditCardSubmit('applyPlatinumVisa')}
                >
                  Apply
                </button>
              </div>
            )}

            {offers.smart_rewards_offer && (
              <div className="offer">
                <h2>Smart Rewards Credit Card</h2>
                <img src="/SMART.png" alt="SMART" />
                <p>Earn unlimited rewards.</p>
                <p>Enjoy no annual fee, no annual point limit, and you can redeem points for cash. Our quick and easy application process takes just a few minutes to complete.
                </p>
                <p><strong>Good For:</strong> Earning 1 point for every $1 spent, Everyday purchases</p>
                <a href="https://www.mymax.com/assets/files/sCGyk5zF" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                <button 
                  id="SmartRewards" 
                  className="apply-btn"
                  onClick={() => handleCreditCardSubmit('SmartRewards')}
                >
                  Apply
                </button>
              </div>
            )}

            {offers.HELOC_offer && (
              <div className="offer">
                <h2>HELOC</h2>
                <img src="/HELOC.png" alt="HELOC" />
                <p>Rates and terms vary. Click Learn More to speak with a lending specialist.</p>
                <button 
                  id="HELOC" 
                  className="apply-btn"
                  onClick={() => window.open('https://www.mymax.com/heloc', '_blank')}
                >
                  Learn More
                </button>
              </div>
            )}

            {offers.checking_offer && (
              <div className="offer">
                <h2>Checking</h2>
                <img src="/NEWCHECKING.png" alt="NEWCHECKING" />
                <p>MAX has checking options for every stage of life. We make managing your money simple and
                  convenient.</p>
                <button 
                  id="Checking" 
                  className="apply-btn"
                  onClick={() => window.open('https://www.mymax.com/spend/personal/checking-accounts', '_blank')}
                >
                  Learn More
                </button>
              </div>
            )}

            {offers.wealth_management_offer && (
              <div className="offer">
                <h2>Wealth Management</h2>
                <img src="/CHECKING.png" alt="CHECKING" />
                <p>Our selection of investment opportunities is designed to help you with investing and saving
                  for the future no matter what stage of life you're in.</p>
                <button 
                  id="WealthManagement" 
                  className="apply-btn"
                  onClick={() => window.open('https://www.mymax.com/save/wealth-management', '_blank')}
                >
                  Learn More
                </button>
              </div>
            )}

            {offers.insurance_offer && (
              <div className="offer">
                <h2>Insurance</h2>
                <img src="/INSURE.png" alt="INSURE" />
                <p>MAX offers customized insurance protection for almost everything you cherish at a great
                  price.</p>
                <button 
                  id="Insurance" 
                  className="apply-btn"
                  onClick={() => window.open('https://www.mymax.com/insurance', '_blank')}
                >
                  Learn More
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LoanOffersPage;
